import productCardLocales from "@/lib/localization/ProductCard.locales";
import {
  Body,
  Button,
  Caption,
  FlexContainer,
  Icon,
  useCartCount,
  useLocales,
  type ProductListType,
} from "@/lib/main";
import { CSSProperties, useEffect, useState } from "react";
import AddRemoveProduct from "./AddRemoveProduct";
import Rating from "./rating/Rating";

type Props = ProductListType;

type UserType = {
  favorites?: string[];
  [key: string]: unknown;
};

export default function ProductCard({
  id,
  name,
  brand,
  size,
  description,
  image,
  price,
  originalPrice,
  rating,
}: Props) {
  const l = useLocales();
  const { setCartCount } = useCartCount();
  const [count, setCount] = useState(0);
  const [favorite, setFavorite] = useState(false);

  const handleCountChange = (countChange: number) => {
    const cart: { [key: string]: number } = JSON.parse(
      localStorage.getItem("cart") || "{}",
    );
    cart[id] = (cart[id] || 0) + countChange;
    setCount(cart[id]);
    if (cart[id] <= 0) delete cart[id];

    localStorage.setItem("cart", JSON.stringify(cart));

    const totalItems = Object.values(cart).reduce(
      (acc: number, quantity: number) => acc + quantity,
      0,
    );
    setCartCount(totalItems);
  };

  // TODO: add favorites to db and remove from localStorage
  const handleFavoriteClick = () => {
    const user: UserType = JSON.parse(localStorage.getItem("user") || "{}");
    if (favorite)
      user.favorites = (user.favorites || []).filter(
        (favoriteId: string) => favoriteId !== id,
      );
    else user.favorites = [...(user.favorites || []), id];

    localStorage.setItem("user", JSON.stringify(user));

    setFavorite(!favorite);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    queueMicrotask(() => {
      const cart: Record<string, number> = JSON.parse(
        localStorage.getItem("cart") || "{}",
      );
      setCount(cart[id] || 0);

      const user: UserType = JSON.parse(localStorage.getItem("user") || "{}");
      setFavorite(user.favorites?.includes(id) || false);
    });
  }, [id]);

  const articleStyles: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "var(--spacing-sm)",

    backgroundColor: "var(--color-background-frame)",
    padding: "var(--spacing-md)",
    borderRadius: "var(--border-radius-md)",
  };

  const imgContainerStyles: CSSProperties = {
    borderRadius: "var(--border-radius-sm)",
    position: "relative",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
  };

  const imgStyle: CSSProperties = {
    width: "8.25rem",
    aspectRatio: "1920/1080",
  };

  const favoriteButtonStyle: CSSProperties = {
    backgroundColor: "transparent",
    border: "none",
    position: "absolute",
    padding: "var(--spacing-md)",
    top: "0",
    insetInlineStart: "0",
  };

  return (
    <article style={articleStyles}>
      <div onClick={handleFavoriteClick} style={imgContainerStyles}>
        <button style={favoriteButtonStyle}>
          <Icon name={favorite ? "heart-solid" : "heart"} color="brand" />
        </button>
        <img src={image} style={imgStyle} alt={l(name)} />
      </div>

      <Caption>
        {l(name)}
        {brand ? ` | ${l(brand)}` : null}
        {size ? ` | ${l(size)}` : null}
        {description ? ` | ${l(description)}` : null}
      </Caption>

      <FlexContainer gap="sm" justify="space-between" fullWidth>
        <FlexContainer gap="xs">
          <Body fontWeight={500} size="md">
            {price}
          </Body>

          <Caption>{l(productCardLocales.currency)}</Caption>

          {originalPrice ? (
            <Caption
              color="tertiary"
              style={{ textDecorationLine: "line-through" }}
            >
              {originalPrice}
            </Caption>
          ) : null}
        </FlexContainer>

        {rating ? <Rating id={id} rating={rating} /> : null}
      </FlexContainer>

      <FlexContainer fullWidth gap="lg" justify="space-between">
        <Button
          color="brand"
          fullWidth={count > 0 ? undefined : true}
          onClick={() => handleCountChange(1)}
          size="sm"
        >
          <Icon color="white" name="shopping-cart" size="md" />
          {count > 0 ? count : null}{" "}
          {count > 0
            ? l(productCardLocales.inCart)
            : l(productCardLocales.addToCart)}{" "}
        </Button>

        {count > 0 ? (
          <AddRemoveProduct
            count={count}
            handleCountChange={handleCountChange}
          />
        ) : null}
      </FlexContainer>
    </article>
  );
}
